/**
 * Admin Dashboard Page
 * Review and approve/reject blog drafts
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, CheckCircle, XCircle, Eye, Calendar, Tag, ExternalLink, Loader2, Quote, Trash2, Clock, FileText, Instagram } from 'lucide-react';
import { isAuthenticated, logout } from '@/utils/auth';
import AdminLogin from '@/components/AdminLogin';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Components } from 'react-markdown';

interface BlogDraft {
  id: string;
  title: string;
  slug: string;
  content: string;
  summary: string;
  imageUrl: string;
  imagePrompt: string;
  keywords: string[];
  sources: Array<{
    title: string;
    url: string;
    source: string;
  }>;
  instagram?: {
    imageUrl: string;
    caption: string;
    posted: boolean;
    postedAt: Date | null;
    postId: string | null;
    approved: boolean;
    approvedAt: Date | null;
  };
  status: string;
  createdAt: string;
  weekOf: string;
}

export default function Admin() {
  const [authenticated, setAuthenticated] = useState(false);
  const [drafts, setDrafts] = useState<BlogDraft[]>([]);
  const [publishedBlogs, setPublishedBlogs] = useState<BlogDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewDraft, setPreviewDraft] = useState<BlogDraft | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedSources, setExpandedSources] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    const auth = isAuthenticated();
    setAuthenticated(auth);
    
    if (auth) {
      fetchDrafts();
      fetchPublishedBlogs();
    }
  }, []);

  const fetchDrafts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/blog/drafts');
      const data = await response.json();
      
      if (data.success) {
        setDrafts(data.data);
      } else {
        setError('Failed to fetch drafts');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Fetch drafts error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublishedBlogs = async () => {
    try {
      const response = await fetch('/api/blog/published');
      const data = await response.json();
      
      if (data.success) {
        setPublishedBlogs(data.data);
      }
    } catch (err) {
      console.error('Fetch published blogs error:', err);
    }
  };

  const handleApprove = async (draftId: string) => {
    if (!confirm('Approve and publish this blog post?')) {
      return;
    }

    setActionLoading(draftId);
    setError('');

    try {
      const response = await fetch(`/api/blog/approve/${draftId}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        alert('Blog approved and published successfully!');
        // Remove from drafts list and refresh published blogs
        setDrafts(drafts.filter(d => d.id !== draftId));
        fetchPublishedBlogs();
      } else {
        setError(data.error || 'Failed to approve blog');
      }
    } catch (err) {
      setError('Error approving blog');
      console.error('Approve error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (blogId: string, isPublished: boolean) => {
    if (!confirm('Are you sure you want to delete this blog? This action cannot be undone.')) {
      return;
    }

    setActionLoading(blogId);
    setError('');

    try {
      const response = await fetch(`/api/blog/reject/${blogId}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        alert('Blog deleted successfully');
        // Remove from appropriate list
        if (isPublished) {
          setPublishedBlogs(publishedBlogs.filter(b => b.id !== blogId));
        } else {
          setDrafts(drafts.filter(d => d.id !== blogId));
        }
      } else {
        setError(data.error || 'Failed to delete blog');
      }
    } catch (err) {
      setError('Error deleting blog');
      console.error('Delete error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveInstagram = async (blogId: string) => {
    if (!confirm('Approve this Instagram post? It will be marked as ready to post.')) {
      return;
    }

    setActionLoading(`ig-approve-${blogId}`);
    setError('');

    try {
      const response = await fetch(`/api/instagram/approve/${blogId}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        alert('Instagram post approved! You can now post it manually.');
        // Refresh drafts to show updated status
        await fetchDrafts();
        await fetchPublishedBlogs();
      } else {
        setError(data.error || 'Failed to approve Instagram post');
      }
    } catch (err) {
      setError('Error approving Instagram post');
      console.error('Instagram approve error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectInstagram = async (blogId: string) => {
    if (!confirm('Reject this Instagram post? You can regenerate it later.')) {
      return;
    }

    setActionLoading(`ig-reject-${blogId}`);
    setError('');

    try {
      const response = await fetch(`/api/instagram/reject/${blogId}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        alert('Instagram post rejected');
        // Refresh drafts to show updated status
        await fetchDrafts();
        await fetchPublishedBlogs();
      } else {
        setError(data.error || 'Failed to reject Instagram post');
      }
    } catch (err) {
      setError('Error rejecting Instagram post');
      console.error('Instagram reject error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePostToInstagram = async (blogId: string) => {
    if (!confirm('Post this to Instagram NOW? Make sure you\'ve reviewed it thoroughly.')) {
      return;
    }

    setActionLoading(`ig-post-${blogId}`);
    setError('');

    try {
      const response = await fetch(`/api/instagram/post/${blogId}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        alert(`Posted to Instagram successfully! Post ID: ${data.postId}`);
        // Refresh drafts to show updated status
        await fetchDrafts();
        await fetchPublishedBlogs();
      } else {
        setError(data.error || 'Failed to post to Instagram');
      }
    } catch (err) {
      setError('Error posting to Instagram');
      console.error('Instagram post error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (draftId: string) => {
    const reason = prompt('Reason for rejection (optional):');
    if (reason === null) return; // Cancelled

    setActionLoading(draftId);
    setError('');

    try {
      const response = await fetch(`/api/blog/reject/${draftId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason }),
      });

      const data = await response.json();

      if (data.success) {
        alert('Blog rejected successfully');
        // Remove from drafts list
        setDrafts(drafts.filter(d => d.id !== draftId));
      } else {
        setError(data.error || 'Failed to reject blog');
      }
    } catch (err) {
      setError('Error rejecting blog');
      console.error('Reject error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleGenerateBlog = async () => {
    if (!confirm('Generate a new blog for today? This will pull the latest news and create a draft.')) {
      return;
    }

    setActionLoading('generate');
    setError('');

    try {
      const response = await fetch('/api/blog/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          postDate: new Date().toISOString(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert(`Blog generated successfully: "${data.data.title}". Check your email or refresh to see the draft.`);
        // Refresh drafts list
        fetchDrafts();
      } else {
        setError(data.error || 'Failed to generate blog');
      }
    } catch (err) {
      setError('Error generating blog');
      console.error('Generate blog error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    logout();
    setAuthenticated(false);
  };

  if (!authenticated) {
    return <AdminLogin onLoginSuccess={() => setAuthenticated(true)} />;
  }

  const renderBlogCard = (blog: BlogDraft, isPending: boolean) => (
    <Card key={blog.id} className="overflow-hidden">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg sm:text-xl mb-2 break-words">{blog.title}</CardTitle>
            <CardDescription className="break-words">{blog.summary}</CardDescription>
          </div>
          <Badge variant={isPending ? 'secondary' : 'default'} className="self-start shrink-0">
            {isPending ? 'Pending' : 'Published'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Image Preview */}
        {blog.imageUrl && (
          <div className="rounded-lg overflow-hidden border">
            <img
              src={blog.imageUrl}
              alt={blog.title}
              className="w-full h-32 sm:h-48 object-cover"
            />
          </div>
        )}

        {/* Meta Information */}
        <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
            {new Date(blog.createdAt).toLocaleDateString('en-AU')}
          </div>
          <div className="flex items-center gap-1">
            <Tag className="h-3 w-3 sm:h-4 sm:w-4" />
            {blog.keywords.length} keywords
          </div>
        </div>

        {/* Keywords */}
        {blog.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {blog.keywords.slice(0, 3).map((keyword, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {keyword}
              </Badge>
            ))}
            {blog.keywords.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{blog.keywords.length - 3} more
              </Badge>
            )}
          </div>
        )}

        {/* Sources */}
        <div>
          <p className="text-xs sm:text-sm font-medium mb-2">News Sources ({blog.sources.length}):</p>
          <div className="space-y-1">
            {blog.sources.slice(0, 3).map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:underline break-all"
              >
                <ExternalLink className="h-3 w-3 shrink-0" />
                <span className="truncate">{source.source}: {source.title}</span>
              </a>
            ))}
            {blog.sources.length > 3 && expandedSources !== blog.id && (
              <button
                onClick={() => setExpandedSources(blog.id)}
                className="text-xs text-blue-600 hover:underline font-medium"
              >
                +{blog.sources.length - 3} more sources
              </button>
            )}
            {expandedSources === blog.id && (
              <>
                {blog.sources.slice(3).map((source, idx) => (
                  <a
                    key={idx + 3}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:underline break-all"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">{source.source}: {source.title}</span>
                  </a>
                ))}
                <button
                  onClick={() => setExpandedSources(null)}
                  className="text-xs text-muted-foreground hover:underline"
                >
                  Show less
                </button>
              </>
            )}
          </div>
        </div>

        {/* Instagram Post Preview Section */}
        {blog.instagram && (
          <div className="mt-6 border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Instagram className="h-5 w-5 text-pink-600" />
              <h3 className="font-semibold">Instagram Post</h3>
              {blog.instagram.posted && (
                <Badge className="bg-pink-600">Posted</Badge>
              )}
              {blog.instagram.approved && !blog.instagram.posted && (
                <Badge variant="default">Approved - Ready to Post</Badge>
              )}
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Image Preview */}
              <div>
                <img 
                  src={blog.instagram.imageUrl} 
                  alt="Instagram preview"
                  className="w-full rounded-lg border-2 border-gray-200"
                />
              </div>
              
              {/* Caption Preview */}
              <div>
                <div className="bg-gray-50 p-4 rounded-lg border max-h-80 overflow-y-auto">
                  <p className="text-sm font-mono whitespace-pre-wrap">
                    {blog.instagram.caption}
                  </p>
                </div>
                
                {/* Instagram Approval Actions */}
                {!blog.instagram.posted && !blog.instagram.approved && (
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => handleApproveInstagram(blog.id)}
                      disabled={actionLoading === `ig-approve-${blog.id}`}
                      className="flex-1 bg-pink-600 hover:bg-pink-700"
                      size="sm"
                    >
                      {actionLoading === `ig-approve-${blog.id}` ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Approve Instagram
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleRejectInstagram(blog.id)}
                      disabled={actionLoading === `ig-reject-${blog.id}`}
                      size="sm"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}
                
                {blog.instagram.approved && !blog.instagram.posted && (
                  <Button
                    onClick={() => handlePostToInstagram(blog.id)}
                    disabled={actionLoading === `ig-post-${blog.id}`}
                    className="w-full mt-4 bg-pink-600 hover:bg-pink-700"
                    size="sm"
                  >
                    {actionLoading === `ig-post-${blog.id}` ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Instagram className="h-4 w-4 mr-2" />
                    )}
                    Post to Instagram Now
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          onClick={() => setPreviewDraft(blog)}
          className="w-full sm:w-auto"
          size="sm"
        >
          <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
          Preview
        </Button>
        {isPending ? (
          <>
            <Button
              variant="default"
              onClick={() => handleApprove(blog.id)}
              disabled={actionLoading === blog.id}
              className="w-full sm:w-auto"
              size="sm"
            >
              {actionLoading === blog.id ? (
                <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              )}
              Approve
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleReject(blog.id)}
              disabled={actionLoading === blog.id}
              className="w-full sm:w-auto"
              size="sm"
            >
              <XCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
              Reject
            </Button>
          </>
        ) : (
          <Button
            variant="destructive"
            onClick={() => handleDelete(blog.id, true)}
            disabled={actionLoading === blog.id}
            className="w-full sm:w-auto"
            size="sm"
          >
            {actionLoading === blog.id ? (
              <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
            )}
            Delete
          </Button>
        )}
      </CardFooter>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header - Mobile Optimized */}
      <div className="border-b bg-card sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0 mr-2">
            <h1 className="text-lg sm:text-2xl font-bold truncate">Blog Admin</h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">Manage blog posts</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button 
              variant="default" 
              onClick={handleGenerateBlog} 
              size="sm" 
              disabled={actionLoading === 'generate'}
              className="hidden sm:flex"
            >
              {actionLoading === 'generate' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Blog
                </>
              )}
            </Button>
            <Button 
              variant="default" 
              onClick={handleGenerateBlog} 
              size="sm" 
              disabled={actionLoading === 'generate'}
              className="sm:hidden"
            >
              {actionLoading === 'generate' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <FileText className="h-4 w-4" />
              )}
            </Button>
            <Button variant="outline" onClick={handleLogout} size="sm">
              <LogOut className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content - Mobile Optimized */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {error && (
          <Alert variant="destructive" className="mb-4 sm:mb-6">
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full grid grid-cols-2 mb-4 sm:mb-6">
            <TabsTrigger value="pending" className="text-xs sm:text-sm">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Pending ({drafts.length})
            </TabsTrigger>
            <TabsTrigger value="published" className="text-xs sm:text-sm">
              <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              Published ({publishedBlogs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : drafts.length === 0 ? (
              <Card>
                <CardContent className="py-8 sm:py-12 text-center">
                  <Clock className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-muted-foreground" />
                  <p className="text-sm sm:text-base text-muted-foreground">No pending blog drafts</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                    New drafts will appear here when generated
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {drafts.map((draft) => renderBlogCard(draft, true))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="published">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : publishedBlogs.length === 0 ? (
              <Card>
                <CardContent className="py-8 sm:py-12 text-center">
                  <FileText className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-muted-foreground" />
                  <p className="text-sm sm:text-base text-muted-foreground">No published blogs</p>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-2">
                    Published blogs will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {publishedBlogs.map((blog) => renderBlogCard(blog, false))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview Modal - Full Blog Post Rendering */}
      <Dialog open={!!previewDraft} onOpenChange={() => setPreviewDraft(null)}>
        <DialogContent className="max-w-full sm:max-w-6xl max-h-[90vh] p-0 m-2 sm:m-4">
          <ScrollArea className="h-[90vh]">
            {previewDraft && (
              <article className="px-4 sm:px-8 py-4 sm:py-6">
                <div className="max-w-4xl mx-auto">
                  
                  {/* Header Section */}
                  <header className="mb-6 sm:mb-8 space-y-4 sm:space-y-6">
                    {/* Meta Information */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                        <time>{new Date(previewDraft.createdAt).toLocaleDateString('en-AU')}</time>
                      </div>
                      <Badge variant="secondary">Preview</Badge>
                    </div>

                    {/* Title */}
                    <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight break-words">
                      {previewDraft.title}
                    </h1>

                    {/* Summary */}
                    <p className="text-base sm:text-xl text-muted-foreground leading-relaxed break-words">
                      {previewDraft.summary}
                    </p>

                    {/* Keywords */}
                    {previewDraft.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {previewDraft.keywords.map((keyword, idx) => (
                          <Badge 
                            key={idx} 
                            variant="secondary" 
                            className="px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium"
                          >
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Decorative divider */}
                    <div className="w-16 sm:w-20 h-1 bg-primary/20 rounded-full mt-4 sm:mt-6"></div>
                  </header>

                  {/* Hero Image */}
                  <div className="mb-8 sm:mb-12">
                    <div className="aspect-video rounded-xl overflow-hidden shadow-lg border-2 border-muted">
                      <img
                        src={previewDraft.imageUrl}
                        alt={previewDraft.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-6 sm:space-y-8 mb-12 sm:mb-16">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h2: ({node, ...props}) => (
                          <div className="mt-8 sm:mt-16 mb-6 sm:mb-8 first:mt-0">
                            <h2 className="text-xl sm:text-3xl font-bold tracking-tight border-l-4 border-primary pl-3 sm:pl-4" {...props} />
                          </div>
                        ),
                        
                        p: ({node, ...props}) => {
                          const text = props.children?.toString() || '';
                          
                          if (text.startsWith('POV:')) {
                            return (
                              <Card className="my-6 sm:my-8 border-l-4 border-l-primary bg-primary/5">
                                <CardContent className="py-4 sm:py-6">
                                  <div className="flex gap-3 sm:gap-4">
                                    <Quote className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0 mt-1" />
                                    <p className="text-base sm:text-lg leading-relaxed m-0 font-medium break-words" {...props} />
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          }
                          
                          if (text.includes('takeaway') || text.includes('bottom line') || text.includes('key point')) {
                            return (
                              <div className="my-6 sm:my-8 p-4 sm:p-6 bg-primary/5 rounded-lg border-l-4 border-primary">
                                <p className="text-base sm:text-lg leading-relaxed m-0 font-medium break-words" {...props} />
                              </div>
                            );
                          }
                          
                          return (
                            <p className="text-base sm:text-lg leading-relaxed my-4 sm:my-6 text-foreground/90 break-words" {...props} />
                          );
                        },
                        
                        a: ({node, ...props}) => (
                          <a 
                            className="text-primary font-semibold hover:underline decoration-2 underline-offset-4 transition-all inline-flex items-center gap-1 break-all" 
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          >
                            {props.children}
                            <ExternalLink className="h-3 w-3 inline flex-shrink-0" />
                          </a>
                        ),
                        
                        strong: ({node, ...props}) => (
                          <strong className="font-bold text-foreground" {...props} />
                        ),
                        
                        ul: ({node, ...props}) => (
                          <ul className="my-4 sm:my-6 space-y-2 sm:space-y-3 text-base sm:text-lg" {...props} />
                        ),
                        
                        li: ({node, ...props}) => (
                          <li className="leading-relaxed pl-1 sm:pl-2 break-words" {...props} />
                        ),
                      } as Components}
                    >
                      {previewDraft.content}
                    </ReactMarkdown>
                  </div>

                  {/* Sources Section */}
                  {previewDraft.sources.length > 0 && (
                    <div className="mb-12 sm:mb-16">
                      <Card className="border-2 shadow-sm">
                        <CardContent className="pt-4 sm:pt-6">
                          <Accordion type="single" collapsible defaultValue="sources">
                            <AccordionItem value="sources" className="border-none">
                              <AccordionTrigger className="hover:no-underline py-3 sm:py-4">
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                                  </div>
                                  <div className="text-left">
                                    <div className="font-bold text-sm sm:text-lg">News Sources</div>
                                    <div className="text-xs sm:text-sm text-muted-foreground">{previewDraft.sources.length} articles referenced</div>
                                  </div>
                                </div>
                              </AccordionTrigger>
                              <AccordionContent className="pt-3 sm:pt-4">
                                <div className="space-y-3 sm:space-y-4 pl-0 sm:pl-13">
                                  {previewDraft.sources.map((source, idx) => (
                                    <a
                                      key={idx}
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block p-3 sm:p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-all group"
                                    >
                                      <div className="flex items-start gap-2 sm:gap-3">
                                        <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5 mt-0.5 flex-shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
                                        <div className="flex-1 min-w-0">
                                          <div className="font-semibold text-xs sm:text-base group-hover:text-primary transition-colors mb-1 break-words">
                                            {source.title}
                                          </div>
                                          <div className="text-xs sm:text-sm text-muted-foreground truncate">{source.source}</div>
                                        </div>
                                      </div>
                                    </a>
                                  ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              </article>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

